import useTimelineDeleteFlow from './hooks/useTimelineDeleteFlow';
import React from 'react';
import { Box, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../services/firebase';
import UnifiedTimelineEntry from './UnifiedTimelineEntry';
import {
  getCaregiverLabel,
  getResolvedCaregiverRole,
} from './utils/timelineCaregiverLabels';
import SourceOriginalNotePanel from './SourceOriginalNotePanel';
import CaptureFirstContextDetails from './CaptureFirstContextDetails';
import TimelineEntryCareDetails from '../../features/careSourceProcessing/TimelineEntryCareDetails';
import { buildTimelineStoryPresentation } from './utils/timelineStorySections';
import StoryCard from './StoryCard';
import {
  CARE_RECORD_VISIBILITY,
  canManageCareRecordPrivacy,
  isCareRecordPrivate,
} from '../../services/timeline/recordVisibility';
import { buildHistoricalSourceReceipt } from './utils/sourceStoryReceipt';
import useCareSourceReorganization from '../../features/careSourceProcessing/useCareSourceReorganization';
import StoryMomentParentInline from './StoryMomentParentInline';
import useTimelineMedicationReview from './hooks/useTimelineMedicationReview';
import TimelineMedicationReviewSheet from './TimelineMedicationReviewSheet';

const SourceNoteTimelineGroup = ({
  actionMenuDisabled,
  categoryGroup,
  currentUserId = null,
  currentUserRole = null,
  getEntryPresentation,
  showTimelineEntries = true,
  showOriginalPanel = true,
  showSourceReviewActions = true,
  onDeleteEntry,
  onEditEntry,
  child = null,
  requireKnowledgeItems = false,
  onHasContent = null,
  defaultExpanded = true,
  expandedStoryParentId = '',
  onToggleStoryParent = null,
}) => {
  const navigate = useNavigate();
  const storyItem = categoryGroup.storyItem || null;
  const sourceReceiptOnly = Boolean(storyItem?.sourceReceiptOnly);
  const storyScenes = React.useMemo(
    () => (Array.isArray(storyItem?.sourceStoryScenes) ? storyItem.sourceStoryScenes : []),
    [storyItem?.sourceStoryScenes]
  );
  const entries = storyScenes.length ? storyScenes : [...(categoryGroup.entries || [])];
  const isReferenceOnlyGroup = entries.length > 0 && entries.every((entry) => entry.kind === 'story_moment_reference' || entry.pointerKind === 'story_moment_reference');
  const originalNote = categoryGroup.sourceContext?.originalNote || storyItem?.sourceStoryContext?.originalNote || '';
  const storyPresentation = React.useMemo(
    () => buildTimelineStoryPresentation(storyScenes, { originalNote }),
    [originalNote, storyScenes]
  );
  const storySections = storyPresentation.sections;
  const storySummary = storyPresentation.summary;
  const historicalSourceReceiptSummary = React.useMemo(
    () => storyItem?.sourceReceiptSummary || buildHistoricalSourceReceipt(storyItem || {})?.summary || '',
    [storyItem]
  );
  const sourceAttachments = React.useMemo(
    () => categoryGroup.sourceContext?.sourceAttachments || [],
    [categoryGroup.sourceContext?.sourceAttachments]
  );
  const sourceNoteId = categoryGroup.sourceContext?.sourceNoteId || '';
  const sourceNotePath = categoryGroup.sourceContext?.sourceNotePath || '';
  const captureDetailsChildId = child?.id || entries[0]?.childId || storyItem?.childId || '';
  const [panelHasContent, setPanelHasContent] = React.useState(!requireKnowledgeItems);
  const [cardExpanded, setCardExpanded] = React.useState(
    Boolean(defaultExpanded) && !historicalSourceReceiptSummary
  );
  const [storyPrivacySaving, setStoryPrivacySaving] = React.useState(false);
  const storyDeleteFlow = useTimelineDeleteFlow({ childId: child?.id });
  const storyDeleteSaving = Boolean(storyDeleteFlow.actionLoadingId);
  const medicationReviewEntry = entries.find((entry) => entry?.id) || storyItem || {};
  const sourceMedicationReview = useTimelineMedicationReview({
    entryId: medicationReviewEntry.id || sourceNoteId,
    entryData: {
      ...medicationReviewEntry,
      childId: medicationReviewEntry.childId || captureDetailsChildId,
    },
  });

  React.useEffect(() => {
    setPanelHasContent(!requireKnowledgeItems);
    setCardExpanded(Boolean(defaultExpanded) && !historicalSourceReceiptSummary);
  }, [defaultExpanded, historicalSourceReceiptSummary, requireKnowledgeItems, sourceNoteId]);

  const handlePanelHasContent = React.useCallback((hasContent) => {
    setPanelHasContent(Boolean(hasContent) || !requireKnowledgeItems);
    onHasContent?.(Boolean(hasContent));
  }, [onHasContent, requireKnowledgeItems]);

  const handleToggleCard = React.useCallback(() => {
    setCardExpanded((current) => !current);
  }, []);

  const storyIsPrivate = storyScenes.length > 0 && storyScenes.every((scene) => isCareRecordPrivate(scene));
  const canToggleStoryPrivacy = !sourceReceiptOnly && storyScenes.length > 0 && canManageCareRecordPrivacy(currentUserRole);
  const canReorganizeStory = !sourceReceiptOnly && Boolean(sourceNoteId) && canManageCareRecordPrivacy(currentUserRole);
  const reorganization = useCareSourceReorganization({
    enabled: canReorganizeStory,
    sourceNoteId,
    scenes: storyScenes,
  });

  const handleToggleStoryPrivacy = React.useCallback(async () => {
    if (!canToggleStoryPrivacy || storyPrivacySaving) {
      return;
    }

    const nextVisibility = storyIsPrivate
      ? CARE_RECORD_VISIBILITY.SHARED
      : CARE_RECORD_VISIBILITY.PRIVATE;

    setStoryPrivacySaving(true);

    try {
      await Promise.all(storyScenes
        .filter((scene) => scene?.id)
        .map((scene) => updateDoc(doc(db, scene.collection || 'dailyLogs', scene.id), {
          visibility: nextVisibility,
          updatedAt: serverTimestamp(),
          privacyUpdatedAt: serverTimestamp(),
          privacyUpdatedBy: auth.currentUser?.uid || null,
        })));

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
          detail: {
            childId: storyScenes[0]?.childId || null,
            entryId: sourceNoteId || null,
          },
        }));
      }
    } catch (privacyError) {
      console.error('Error updating source story privacy:', privacyError);
    } finally {
      setStoryPrivacySaving(false);
    }
  }, [canToggleStoryPrivacy, sourceNoteId, storyIsPrivate, storyPrivacySaving, storyScenes]);

  const canDeleteStory = !sourceReceiptOnly && storyScenes.length > 0 && (currentUserRole === 'care_owner' || storyScenes.every(scene => (scene.createdBy || scene.authorId) === auth.currentUser?.uid));
  const handleDeleteStory = () => storyDeleteFlow.handleDeleteEntry({ id: sourceNoteId, sourceNoteId, childId: child?.id, kind: 'story' });

  const renderOriginalPanel = React.useCallback((
    showActions = true,
    originalNoteValue = originalNote,
    showOriginalNoteSection = true,
    variant = 'inline'
  ) => (
    <div style={{ display: panelHasContent ? 'block' : 'none' }}>
      <SourceOriginalNotePanel
        originalNote={originalNoteValue}
        sourceAttachments={sourceAttachments}
        sourceNoteId={sourceNoteId}
        sourceNotePath={sourceNotePath}
        captureDetailsKey={sourceNoteId}
        captureDetailsChildId={captureDetailsChildId}
        showSourceReviewActions={showActions}
        showGenericCareDetails={false}
        showOriginalNoteSection={showOriginalNoteSection}
        requireKnowledgeItems={requireKnowledgeItems}
        onHasReviewContent={handlePanelHasContent}
        onReviewMedication={sourceMedicationReview.open}
        appointmentTimeZone={child?.timeZone || child?.timezone || ''}
        canManageAppointments={['care_owner', 'care_partner'].includes(currentUserRole)}
        variant={variant}
      />
    </div>
  ), [captureDetailsChildId, handlePanelHasContent, originalNote, panelHasContent, requireKnowledgeItems, sourceAttachments, sourceMedicationReview.open, sourceNoteId, sourceNotePath]);

  const getEntryOriginalNote = (entry = {}) => String(
    entry.metadata?.sourceSpanText
    || entry.source?.sourceSpanText
    || entry.sourceText
    || entry.body
    || entry.note
    || ''
  ).trim();

  const renderEntry = (entry, index, isLast = false) => {
    const isGeneratedStoryMoment = entry.kind === 'story_moment_reference'
      || entry.pointerKind === 'story_moment_reference';

    return (
    <UnifiedTimelineEntry
      key={`${categoryGroup.key}-${entry.id}-${index}`}
      entry={entry}
      presentation={getEntryPresentation(entry)}
      timeString={entry.narrativeTimeLabel || null}
      isLast={isLast}
      showActions={!isGeneratedStoryMoment}
      onEditEntry={isGeneratedStoryMoment ? null : onEditEntry}
      onDeleteEntry={isGeneratedStoryMoment ? null : () => onDeleteEntry(entry)}
      actionMenuDisabled={Boolean(actionMenuDisabled)}
      isEditing={false}
      editText=""
      onEditTextChange={null}
      onSaveEdit={null}
      onCancelEdit={null}
      layoutVariant={storyScenes.length || entry.kind === 'story_moment_reference' ? 'feed' : 'story'}
      nested={Boolean(storyScenes.length)}
      caregiverLabel={getCaregiverLabel(entry)}
      caregiverRole={getResolvedCaregiverRole(entry, currentUserRole, currentUserId)}
      currentUserRole={currentUserRole}
      showCaptureFirstOriginalNote={false}
      showCaptureFirstContextDetails={false}
      suppressPendingMedicationReviewAction
      storyParentExpanded={isGeneratedStoryMoment && expandedStoryParentId === entry.id}
      onStoryParentToggle={isGeneratedStoryMoment ? () => onToggleStoryParent?.(entry.id) : null}
      sourceDetailsNode={isGeneratedStoryMoment ? (
        <StoryMomentParentInline
          entry={entry}
          expanded={expandedStoryParentId === entry.id}
          getEntryPresentation={getEntryPresentation}
          onCollapse={() => onToggleStoryParent?.(entry.id)}
        />
      ) : storyScenes.length || isReferenceOnlyGroup ? null : ({ onReviewMedication }) => (
        <Box sx={{
          mt: 0.55,
        }}>
          <TimelineEntryCareDetails entry={entry} />
          <CaptureFirstContextDetails
            entryData={entry}
            story
            showOriginalNote={false}
            onAddDetails={onEditEntry ? () => onEditEntry(entry) : null}
            onReviewMedication={onReviewMedication}
          />
          {showOriginalPanel ? renderOriginalPanel(showSourceReviewActions && index === entries.length - 1, getEntryOriginalNote(entry) || originalNote, true) : null}
        </Box>
      )}
      showTrackingSuggestion={false}
      showSourceReviewActions={false}
      child={child}
    />
    );
  };

  const renderedEntries = showTimelineEntries && !storyScenes.length
    ? entries.map((entry, index) => renderEntry(entry, index, index === entries.length - 1))
    : null;

  if (sourceReceiptOnly || storyScenes.length) {
    const previewText = storySummary?.text || storyScenes[0]?.body || storyScenes[0]?.title || '';

    return (
      <>
        <StoryCard
          storyItem={storyItem}
          storySections={showTimelineEntries ? storySections : []}
          storySummary={storySummary}
          getEntryPresentation={getEntryPresentation}
          expanded={cardExpanded}
          onToggleExpanded={sourceReceiptOnly && storyItem?.navigationTarget
            ? () => navigate(storyItem.navigationTarget)
            : handleToggleCard}
          previewText={previewText}
          receiptSummary={historicalSourceReceiptSummary}
          titlesOnly={Boolean(historicalSourceReceiptSummary)}
          originalSourceNode={!sourceReceiptOnly && showOriginalPanel
            ? renderOriginalPanel(showSourceReviewActions, originalNote, true, 'header')
            : null}
          menuActions={[
            ...(reorganization.action ? [reorganization.action] : []),
            ...(canToggleStoryPrivacy ? [{
              label: storyIsPrivate ? 'Private' : 'Mark private',
              description: 'Only Care Owners and Partners can see this.',
              checked: storyIsPrivate,
              onClick: handleToggleStoryPrivacy,
              disabled: storyPrivacySaving,
            }] : []),
            ...(canDeleteStory ? [{
              label: 'Delete Story',
              icon: <DeleteIcon />,
              onClick: handleDeleteStory,
              disabled: storyDeleteSaving,
              danger: true,
            }] : []),
          ]}
          showActions={canReorganizeStory || canToggleStoryPrivacy || canDeleteStory}
          actionMenuDisabled={storyPrivacySaving || storyDeleteSaving}
          childName={child?.name || medicationReviewEntry.childName || ''}
        />
        {reorganization.sheet}
        {storyDeleteFlow.deleteConfirmation}
        <TimelineMedicationReviewSheet
          review={sourceMedicationReview}
          childName={child?.name || medicationReviewEntry.childName || ''}
        />
      </>
    );
  }

  return (
    <Stack spacing={{ xs: 1.25, md: 1.55 }}>
      {renderedEntries}
      {!entries.length && showOriginalPanel ? renderOriginalPanel(showSourceReviewActions) : null}
      <TimelineMedicationReviewSheet
        review={sourceMedicationReview}
        childName={child?.name || medicationReviewEntry.childName || ''}
      />
    </Stack>
  );
};

export default SourceNoteTimelineGroup;
